// fetchOrders.js
'use strict';
import parseLinkHeader from 'parse-link-header';

import fetch from '../resource/customFetch.js';
import getMeData from '../selector/getMeData.js';

import getResourceUrl from '../resource/getResourceUrl.js';
import { getHeaders } from '../resource/fetchOptionHeader.js';
import handleFetchError from '../resource/handleFetchError.js';
import objectifyArrayById from '../resource/objectifyArrayById.js';
import { OrderStatus, ServerOrderStatus } from '../resource/shopConstants.js';

import {
  ADD_DIAMOND_PACKS,
  ADD_ORDERS,
  ADD_LIST_ITEMS,
  SET_LIST_ITEMS,
  SET_NETWORKING_FETCHING,
  SET_NETWORKING_SUCCESS,
  SET_NETWORKING_ERROR,
  SET_NETWORKING_FETCHING_BATCH,
  SET_NETWORKING_SUCCESS_BATCH,
  SET_NETWORKING_ERROR_BATCH,
} from '../ActionTypes.js';

/**
 * Fetch orders
 * @kind action
 * @param {array} {productIds} - product ids.
 * @param {OrderStatus} {orderStatus} - order status.
 * @param {ProductType} {productType} - product type.
 * @param {number} {[page = 1]} - page index.
 * @param {number} {[limit = 10]} - page size.
 * @return {Promise} Action promise.
 */
const fetchOrders =
  ({ productIds = [], page = 1, limit = 10, orderStatus, productType } = {}) =>
  async (dispatch, getState) => {
    const orderListSelectPath = ['order', orderStatus, productType];
    const networkingSelectPath = [
      'fetch',
      'order',
      orderStatus,
      productType,
      page,
    ];
    const token = getMeData(getState(), 'token');
    if (!token || !orderStatus || !productType) {
      return dispatch({ type: '' });
    }
    const fetchOptions = {
      method: 'GET',
      headers: {
        ...getHeaders(),
        Authorization: `Bearer ${token}`,
      },
    };
    const fetchUrl = getResourceUrl({ endpoint: '/orders' });
    fetchUrl.searchParams.append('page', page);
    fetchUrl.searchParams.append('limit', limit);
    fetchUrl.searchParams.append('type', productType);
    if (productIds.length) {
      productIds.forEach(productId =>
        fetchUrl.searchParams.append('product_id', productId)
      );
    }
    switch (orderStatus) {
      case OrderStatus.BOUGHT: {
        fetchUrl.searchParams.append('status', ServerOrderStatus.PAID);
        fetchUrl.searchParams.append('status', ServerOrderStatus.FULFILLED);
        break;
      }
    }
    dispatch({
      type: productIds.length
        ? SET_NETWORKING_FETCHING_BATCH
        : SET_NETWORKING_FETCHING,
      payload: {
        selectPath: networkingSelectPath,
        ...(productIds.length ? { items: productIds } : {}),
      },
    });
    try {
      let response = await fetch(fetchUrl.href, fetchOptions);
      if (!response.ok) {
        response = await handleFetchError({
          response,
          dispatch,
          getState,
          fetchOptions,
          fetchUrl,
        });
      }
      const payload = (await response.json()) || [];
      const totalCount = parseInt(response.headers.get('x-total-count'), 10);
      const links = parseLinkHeader(response.headers.get('Link'));
      const nextPage = links?.next ? +links.next.page : null;
      const lastPage = links?.last ? +links.last.page : null;
      dispatch({
        type: ADD_ORDERS,
        payload: {
          orders: objectifyArrayById({
            array: payload,
          }),
        },
      });
      dispatch({
        type: page === 1 ? SET_LIST_ITEMS : ADD_LIST_ITEMS,
        payload: {
          selectPath: orderListSelectPath,
          itemIds: payload.map(({ id }) => id),
          nextPage,
          lastPage,
          totalCount,
        },
      });
      const skus = payload
        .map(item => item.items)
        .flat()
        .map(item => {
          const sku = {
            ...item,
            productId: item.product_id,
          };
          delete sku.product_id;
          return sku;
        });
      dispatch({
        type: ADD_DIAMOND_PACKS,
        payload: {
          diamondPacks: objectifyArrayById({
            array: skus,
          }),
        },
      });
      dispatch({
        type: page === 1 ? SET_LIST_ITEMS : ADD_LIST_ITEMS,
        payload: {
          selectPath: [...orderListSelectPath, 'productIds'],
          itemIds: skus.map(({ productId }) => productId),
        },
      });
      return dispatch({
        type: productIds.length
          ? SET_NETWORKING_SUCCESS_BATCH
          : SET_NETWORKING_SUCCESS,
        payload: {
          selectPath: networkingSelectPath,
          ...(productIds.length ? { items: productIds } : {}),
        },
      });
    } catch (error) {
      return dispatch({
        type: productIds.length
          ? SET_NETWORKING_ERROR_BATCH
          : SET_NETWORKING_ERROR,
        payload: {
          selectPath: networkingSelectPath,
          error,
          ...(productIds.length ? { items: productIds } : {}),
        },
      });
    }
  };

export default fetchOrders;
